import dayjs from "dayjs";

import { formatDollars } from "@aclymatepackages/formatters";
import { THERMS_PER_MCF } from "@aclymatepackages/constants";
import { tonsToLbs } from "@aclymatepackages/converters";
import findSubcategory from "@aclymatepackages/subcategories";

import { useCachedDisplayData } from "../../../helpers/firebase";

import DownloadButton from "./DownloadButton";
import { buildMonthYearName } from "./buildMonthYearName.mjs";

import { useAccountData } from "../../../helpers/firebase";
import { convertDataToCsv } from "../../../helpers/otherHelpers";

export const csvColumns = [
  { name: "id", field: "id" }, //in transaction obj
  { name: "Date", field: "date" }, //in transaction obj
  { name: "Name", field: "name" }, //extracted
  { name: "$ Amount", field: "amount" }, //in transaction obj (was "value")
  { name: "Vendor Nickname", field: "vendorNickname" }, //extracted from relationship
  { name: "Vendor Canonical Name", field: "vendorCanonicalName" }, //extracted from v3-companies
  { name: "Description", field: "description" }, //in transaction obj (new)
  { name: "Accounting Category", field: "category" }, //in transaction obj
  { name: "NAICS Code", field: "naicsCode" }, //extracted
  { name: "NAICS Description", field: "naicsTitle" }, //extracted
  { name: "Tons CO2e Per Dollar", field: "tonsCo2ePerDollar" }, //extracted
  { name: "Emission Factor", field: "emissionFactor" }, //extracted
  { name: "Emission Factor Unit", field: "emissionFactorUnit" }, //extracted
  { name: "Emission Factor Source", field: "emissionFactorSource" }, //extracted
  { name: "Emissions Category", field: "subcategory" }, //in transaction obj
  { name: "Scope Three Category", field: "scopeThreeCategory" }, //in transaction obj
  { name: "Tons CO2e", field: "tonsCo2e" }, //in transaction obj
  { name: "Lbs CO2e", field: "lbsCo2" }, //extracted
  { name: "Fuel Type", field: "fuelType" }, //in transaction obj
  { name: "Gas Unit Value", field: "gasUnitValue" }, //in transaction obj
  { name: "Gas Unit", field: "gasUnit" }, //in transaction obj
  { name: "Recurring Emissions Month Fraction", field: "monthFraction" }, //in transaction obj
  { name: "Total Monthly Electric Tons CO2e", field: "totalElectricTons" }, //extracted
  { name: "Total Monthly Gas Tons CO2e", field: "totalGasTons" }, //extracted
  { name: "Total Company Office Tons CO2e", field: "totalCompanyOfficeTons" }, //extracted
  {
    name: "Total Monthly Company Office Electric Tons CO2e",
    field: "companyOfficeElectricTons", //extracted
  },
  {
    name: "Total Monthly Company Office Gas Tons CO2e",
    field: "companyOfficeGasTons", //extracted
  },
  { name: "Total Monthly Home Office Tons CO2e", field: "totalHomeOfficeTons" }, //extracted
  { name: "Monthly Scope 1", field: "scope1Tons" }, //extracted
  { name: "Monthly Scope 2", field: "scope2Tons" }, //extracted
  { name: "Monthly Scope 3", field: "scope3Tons" }, //extracted
  { name: "Scope", field: "scope" }, //in transaction obj
  { name: "Electric Kwh", field: "electricKwh" }, //in transaction obj
  { name: "Therms", field: "therms" }, //extracted
  {
    name: "Renewables Replacement Tons CO2e",
    field: "electricRenewablesReplacementTons", //in transaction obj
  },
  {
    name: "Electric Renewables Percentage",
    field: "electricRenewablesPercentage", //in transaction obj
  },
  { name: "Tons CO2e before renewables", field: "tonsCo2eBeforeRenewables" }, //extracted
  { name: "Office", field: "office" }, //in transaction obj
  { name: "Vehicle", field: "vehicle" }, //in transaction obj
  { name: "Employees", field: "employees" }, //in transaction obj
  { name: "Notes", field: "notes" }, //in transaction obj
];

export const splitUtilitiesEmissions = (emissions) =>
  emissions.flatMap((emission) => {
    const { subcategory, details = {}, date, source } = emission;

    if (subcategory !== "utilities" || source !== "recurring") {
      return emission;
    }

    const {
      companyOfficeElectricTons = 0,
      companyOfficeGasTons = 0,
      totalHomeOfficeTons = 0,
      defaultEmissions = 0,
    } = details;

    const monthYearName = buildMonthYearName(date);
    const totalOtherEmissionsTons = defaultEmissions + totalHomeOfficeTons;

    return [
      {
        date,
        subcategory: "gas",
        name: `${monthYearName} Office Gas Utilities`,
        tonsCo2e: companyOfficeGasTons * -1,
        lbsCo2: tonsToLbs(companyOfficeGasTons),
        scope: 1,
        emissionFactorSource: AGGREGATED_MONTHLY_UTILITIES_SOURCE_NOTE,
      },
      {
        date,
        subcategory: "electricity",
        name: `${monthYearName} Office Electric Utilities`,
        tonsCo2e: companyOfficeElectricTons * -1,
        lbsCo2: tonsToLbs(companyOfficeElectricTons),
        scope: 2,
        emissionFactorSource: AGGREGATED_MONTHLY_UTILITIES_SOURCE_NOTE,
      },
      {
        date,
        subcategory: "home-office",
        name: ` ${monthYearName} Other Utilities`,
        tonsCo2e: totalOtherEmissionsTons * -1,
        lbsCo2: tonsToLbs(totalOtherEmissionsTons),
        scope: 3,
        emissionFactorSource: AGGREGATED_MONTHLY_UTILITIES_SOURCE_NOTE,
      },
    ];
  });

// Purchased heat is entered through the same fuel-type + quantity input as a
// gas bill (its emissions come from the fuel burned), so it carries the same
// gasUnit/gasUnitValue shape and exports the same fuel quantity.
const FUEL_QUANTITY_SUBCATEGORIES = ["gas", "purchased-heat"];

const findGasTherms = (emission) => {
  const { subcategory, emissionData = {} } = emission;

  if (!FUEL_QUANTITY_SUBCATEGORIES.includes(subcategory)) {
    return "";
  }

  const { gasUnit, gasUnitValue, fuelType } = emissionData;

  // Propane's raw reading is gallons, not a natural-gas-equivalent unit --
  // there's no honest therms conversion for it, so this column stays blank
  // the same way it does for genuinely missing gasUnitValue. The raw
  // reading itself is still preserved in the separate Gas Unit/Gas Unit
  // Value columns.
  if (fuelType === "propane" || gasUnit === "gallons") {
    return "";
  }

  if (gasUnit === "mcf") {
    return gasUnitValue * THERMS_PER_MCF;
  }

  if (gasUnit === "ccf") {
    return gasUnitValue * (THERMS_PER_MCF / 100);
  }

  return gasUnitValue;
};

//The "utilities"/"recurring" combined-office row aggregates every
//office/employee's monthly usage into one row -- each of which can carry its
//own state/eGrid, so there is no single emission factor that honestly
//represents the aggregate the way there is for one transaction's bill.
const AGGREGATED_MONTHLY_UTILITIES_SOURCE_NOTE =
  "Aggregated across multiple offices/employees for the month -- no single emission factor applies; see per-transaction utility bills for individual factors";

const isAggregatedMonthlyUtilitiesRow = (subcategory, source) =>
  subcategory === "utilities" && source === "recurring";

const findTonsCo2eBeforeRenewables = (emission) => {
  const { subcategory, details, tonsCo2e } = emission;

  if (subcategory === "home-office") {
    const { homeOfficeElectricRenewablesReplacementTons = 0 } = details || {};

    return tonsCo2e - homeOfficeElectricRenewablesReplacementTons;
  }

  if (subcategory === "electricity") {
    const { companyOfficeElectricRenewablesReplacementTons = 0 } =
      details || {};

    return tonsCo2e - companyOfficeElectricRenewablesReplacementTons;
  }

  return "";
};

export const formatCsvEmissions = (emissions = [], vendorsWithRelationships = []) =>
  emissions.map((emission) => {
    const {
      id,
      date,
      name,
      description = "",
      value = "",
      amount,
      dollarAmount,
      category = "",
      vendorId,
      vendorName,
      naicsCode,
      naicsTitle,
      tonsCo2ePerDollar,
      subcategory,
      source,
      scopeThreeCategory,
      tonsCo2e: rawTonsCo2e,
      fuelType = "",
      monthFraction = "",
      details,
      scope: emissionScope,
      electricKwh = "",
      electricRenewablesReplacementTons = "",
      electricRenewablesPercentage = "",
      office,
      vehicle,
      employees,
      notes = [],
      emissionData = {},
      // Historical transactions can still carry these at the top level --
      // useGasInputProps (helpers/components/inputs.js) falls back the same
      // way, and this exporter did too before emissionData became the only
      // place new rows write them (PR #2192 review finding).
      gasUnitValue: legacyGasUnitValue = "",
      gasUnit: legacyGasUnit = "",
    } = emission;

    const {
      gasUnitValue = legacyGasUnitValue,
      gasUnit = legacyGasUnit,
      emissionFactor: rawEmissionFactor = "",
      emissionFactorUnit: rawEmissionFactorUnit = "",
      emissionFactorSource: rawEmissionFactorSource = "",
    } = emissionData;

    const vendorRelationship = vendorsWithRelationships.find(
      (v) => v?.vendorId === vendorId
    );
    const {
      vendorData = {},
      nickname: vendorNickname = "",
      vendorName: vendorCanonicalName = "",
    } = vendorRelationship || {};

    const {
      naicsCode: vendorNaicsCode = "",
      naicsTitle: vendorNaicsTitle = "",
      tonsCo2ePerDollar: vendorTonsCo2ePerDollar = "",
    } = vendorData;

    const displayVendorName =
      vendorNickname || vendorCanonicalName || vendorName;

    const {
      totalElectricTons = "",
      totalGasTons = "",
      totalCompanyOfficeTons = "",
      companyOfficeElectricTons = "",
      companyOfficeGasTons = "",
      totalHomeOfficeTons = "",
      scope1Tons = "",
      scope2Tons = "",
      scope3Tons = "",
    } = details || {};

    const findTonsCo2e = () => {
      if (!rawTonsCo2e || isNaN(rawTonsCo2e)) {
        return 0;
      }

      return Math.abs(rawTonsCo2e);
    };

    const tonsCo2e = findTonsCo2e();

    const buildNameString = () => {
      if (name) {
        return name;
      }

      if (vendorId) {
        return displayVendorName || vendorName;
      }

      return description || "";
    };

    const { name: categoryName, scope: subcategoryScope } =
      findSubcategory(subcategory);

    const scope = emissionScope || subcategoryScope;

    const buildScopeThreeCategoryString = () => {
      if (subcategory === "spend-based") {
        return vendorData?.scopeThreeCategory || scopeThreeCategory || "";
      }

      if (scope === 3) {
        const { scopeThreeCategory } = findSubcategory(subcategory);
        return scopeThreeCategory || "";
      }

      return "";
    };

    const officeString = office ? office.name : "";
    const vehicleString = vehicle ? vehicle.name : "";
    const employeesString = employees
      ? employees.map(({ name }) => name).join("|")
      : "";

    const transactionAmount = amount || dollarAmount || value || "";

    const returnObj = {
      id,
      date,
      name: buildNameString(),
      amount: formatDollars(transactionAmount),
      vendorNickname: vendorNickname,
      vendorCanonicalName: vendorCanonicalName,
      description: description,
      category: category,
      naicsCode: naicsCode || vendorNaicsCode,
      naicsTitle: naicsTitle || vendorNaicsTitle,
      tonsCo2ePerDollar: tonsCo2ePerDollar || vendorTonsCo2ePerDollar,
      emissionFactor: isAggregatedMonthlyUtilitiesRow(subcategory, source)
        ? ""
        : rawEmissionFactor,
      emissionFactorUnit: isAggregatedMonthlyUtilitiesRow(subcategory, source)
        ? ""
        : rawEmissionFactorUnit,
      emissionFactorSource: isAggregatedMonthlyUtilitiesRow(subcategory, source)
        ? AGGREGATED_MONTHLY_UTILITIES_SOURCE_NOTE
        : rawEmissionFactorSource,
      subcategory: categoryName,
      scopeThreeCategory: buildScopeThreeCategoryString(),
      tonsCo2e,
      lbsCo2e: tonsToLbs(tonsCo2e),
      fuelType,
      gasUnitValue,
      gasUnit,
      monthFraction,
      totalElectricTons,
      totalGasTons,
      totalCompanyOfficeTons,
      companyOfficeElectricTons,
      companyOfficeGasTons,
      totalHomeOfficeTons,
      scope1Tons,
      scope2Tons,
      scope3Tons,
      scope,
      electricKwh,
      therms: findGasTherms(emission),
      electricRenewablesReplacementTons,
      electricRenewablesPercentage,
      tonsCo2eBeforeRenewables: findTonsCo2eBeforeRenewables(emission),
      office: officeString,
      vehicle: vehicleString,
      employees: employeesString,
      notes: notes
        .map(({ text, userName, createdAt }) =>
          `[${userName} - ${dayjs(createdAt).format("MMM D, YYYY")}] ${text}`
        )
        .join("; "),
    };

    return Object.fromEntries(
      Object.entries(returnObj).map(([key, value = ""]) => [key, value])
    );
  });

const EmissionsCsvDownload = ({
  emissions,
  tooltip = "Download Emissions CSV File",
  fileName,
}) => {
  const [vendorsWithRelationships] = useCachedDisplayData(
    "vendor-relationships"
  );
  const [{ name: companyName = "" }] = useAccountData();

  const onEmissionsDownload = () => {
    const csvHeader = csvColumns.map(({ name }) => name).join(",");

    const splitEmissions = splitUtilitiesEmissions(
      formatCsvEmissions(emissions, vendorsWithRelationships)
    );

    const sortedEmissions = [...splitEmissions].sort((a, b) => b.date - a.date);

    return convertDataToCsv(sortedEmissions, csvHeader);
  };

  return (
    <DownloadButton
      tooltip={tooltip}
      buildFile={onEmissionsDownload}
      fileName={
        fileName ||
        `${companyName.split(" ").join("-")}_${dayjs().format(
          "MMDDYYYY"
        )}-aclymate-emissions-download.csv`
      }
    />
  );
};
export default EmissionsCsvDownload;
